import { tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  GetTranslations,
  ResetTranslationSelection,
  SetDisplayedTranslationsCount,
  SetLastModifiedDateToNow,
  SetSelectedTranslations,
  SetTranslations,
  UpdateSelectedTranslation,
} from './translation-table.actions';
import {
  ApplicationSelectorFormValue,
  AppState,
  GetTranslationsWithDateResponse,
  TranslationKeyLanguages,
  TranslationsList,
  TranslationTableStateModel,
} from 'src/app/model';
import { TranslationEditorState } from '../translation-editor/translation-editor.state';
import { StartAppLoading, StopAppLoading } from '../app-loading/app-loading.actions';
import { TranslationEditorService } from '../../service';

@State<TranslationTableStateModel>({
  name: 'translationTable',
  defaults: {
    selected: [],
    translations: {},
    lastModifiedDate: 0,
    displayedTranslationsCount: 0,
  },
})
@Injectable()
export class TranslationTableState {
  private readonly translationEditorService = inject(TranslationEditorService);
  private readonly store = inject(Store);

  @Selector()
  static translations(state: TranslationTableStateModel): TranslationsList {
    return state.translations;
  }

  @Selector()
  static lastModifiedDate(state: TranslationTableStateModel): number {
    return state.lastModifiedDate;
  }

  @Selector()
  static selectedTranslations(state: TranslationTableStateModel): Array<TranslationKeyLanguages> {
    return state.selected;
  }

  @Selector()
  static displayedTranslationsCount(state: TranslationTableStateModel): number {
    return state.displayedTranslationsCount;
  }

  @Action(ResetTranslationSelection)
  resetSelection({ patchState }: StateContext<TranslationTableStateModel>): void {
    patchState({
      selected: [],
    });
  }

  @Action(SetSelectedTranslations)
  setSelectedTranslations(
    { getState, patchState }: StateContext<TranslationTableStateModel>,
    { payload }: { payload: Array<TranslationKeyLanguages> }
  ): void {
    patchState({
      ...getState(),
      selected: payload,
    });
  }

  @Action(UpdateSelectedTranslation)
  updateSelectedTranslation(
    { getState, patchState }: StateContext<TranslationTableStateModel>,
    { payload }: { payload: TranslationKeyLanguages }
  ): void {
    const updateIndex = getState().selected.findIndex(selectedTranslation => selectedTranslation.key === payload.key);
    const selected = getState().selected;
    selected.splice(updateIndex, 1, payload);
    patchState({
      ...getState(),
      selected,
    });
  }

  @Action(SetLastModifiedDateToNow)
  setLastModifiedDateToNow({ patchState }: StateContext<TranslationTableStateModel>): void {
    patchState({
      lastModifiedDate: new Date().getTime(),
    });
  }

  @Action(GetTranslations)
  getTranslations({ getState, patchState }: StateContext<TranslationTableStateModel>, { payload }: { payload: Array<string> }): void {
    const { application, environment } = <ApplicationSelectorFormValue>this.store.selectSnapshot((state: AppState) => state.applicationSelector.applicationForm.model);
    const displayingLanguages: Array<string> = payload.length
      ? payload
      : this.store.selectSnapshot(TranslationEditorState.displayingLanguages);
    this.store.dispatch([new StartAppLoading(), new ResetTranslationSelection()]);
    this.translationEditorService
      .getTranslationsWithDate(application, environment, displayingLanguages)
      .pipe(
        tap(translations => {
          this.store.dispatch([new StopAppLoading(), new SetTranslations(translations)]);
        })
      )
      .subscribe();
  }

  @Action(SetTranslations)
  setTranslations(
    { getState, patchState }: StateContext<TranslationTableStateModel>,
    { payload }: { payload: GetTranslationsWithDateResponse }
  ): void {
    patchState({
      translations: payload.translations,
      lastModifiedDate: payload.lastModifiedDate,
      displayedTranslationsCount: Object.keys(payload.translations).length,
    });
  }

  @Action(SetDisplayedTranslationsCount)
  setDisplayedTranslationsCount(
    { getState, patchState }: StateContext<TranslationTableStateModel>,
    { payload }: { payload: number }
  ): void {
    patchState({
      displayedTranslationsCount: payload,
    });
  }
}
